import { create } from "zustand";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";

// PRD Template Management — Settings → PRD Template Management.
//
// Every mutation re-fetches rather than patching the local list. The server
// enforces one active template per category and may retire another row to
// honour that, so the list after a write is not always the list before it plus
// one change.

export interface PrdTemplateSection {
  key: string;
  title: string;
  required: boolean;
  commercial: boolean;
  order: number;
}

export interface PrdTemplate {
  _id: string;
  name: string;
  category: string;
  description: string;
  active: boolean;
  source: "seeded" | "uploaded";
  sectionCount: number;
  sections: PrdTemplateSection[];
  file: {
    fileName: string;
    mimeType: string;
    size: number;
    uploadedAt: string;
    checksum: string;
  } | null;
  createdByName: string;
  updatedByName: string;
  createdAt: string;
  updatedAt: string;
  usage?: { prdCount: number };
  audit: any[];
}

interface State {
  templates: PrdTemplate[];
  categories: string[];
  missingCategories: string[];
  loading: boolean;
  busy: string;

  fetchAll: () => Promise<void>;
  get: (id: string) => Promise<PrdTemplate | null>;
  save: (form: { name: string; category: string; description: string; file: File }) => Promise<boolean>;
  update: (id: string, patch: { name?: string; category?: string; description?: string }) => Promise<boolean>;
  setActive: (id: string, active: boolean) => Promise<void>;
  remove: (id: string) => Promise<boolean>;
  openFile: (id: string, fileName: string) => Promise<void>;
}

const err = (e: any, fallback: string) => e?.response?.data?.message || fallback;

export const usePrdTemplateStore = create<State>((set, get) => ({
  templates: [],
  categories: [],
  missingCategories: [],
  loading: false,
  busy: "",

  fetchAll: async () => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get("/prd-templates");
      set({
        templates: data.templates || [],
        categories: data.categories || [],
        missingCategories: data.missingCategories || [],
      });
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error(err(e, "Couldn't load the PRD templates"));
      set({ templates: [] });
    } finally {
      set({ loading: false });
    }
  },

  get: async (id) => {
    try {
      const { data } = await axiosInstance.get(`/prd-templates/${id}`);
      return data.template || null;
    } catch (e: any) {
      toast.error(err(e, "Couldn't load the template"));
      return null;
    }
  },

  // Creates, or replaces the category's existing template — the server decides
  // which, so a replacement is never two operations with a gap in between.
  save: async ({ name, category, description, file }) => {
    set({ busy: "save" });
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("category", category);
      fd.append("description", description);
      fd.append("file", file);
      const { data } = await axiosInstance.post("/prd-templates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.message || "Template saved.");
      await get().fetchAll();
      return true;
    } catch (e: any) {
      toast.error(err(e, "Couldn't save the template"));
      return false;
    } finally {
      set({ busy: "" });
    }
  },

  update: async (id, patch) => {
    set({ busy: "update" });
    try {
      const { data } = await axiosInstance.put(`/prd-templates/${id}`, patch);
      toast.success(data.message || "Template updated.");
      await get().fetchAll();
      return true;
    } catch (e: any) {
      toast.error(err(e, "Couldn't update the template"));
      return false;
    } finally {
      set({ busy: "" });
    }
  },

  setActive: async (id, active) => {
    set({ busy: "active" });
    try {
      const { data } = await axiosInstance.patch(`/prd-templates/${id}/active`, { active });
      toast.success(data.message || "Updated.");
      await get().fetchAll();
    } catch (e: any) {
      toast.error(err(e, "Couldn't change the template's status"));
    } finally {
      set({ busy: "" });
    }
  },

  // Returns true when the row is gone. A 409 means it was deactivated instead
  // because PRDs depend on it — that is a result, not a failure, so the
  // message is shown as information.
  remove: async (id) => {
    set({ busy: "delete" });
    try {
      const { data } = await axiosInstance.delete(`/prd-templates/${id}`);
      toast.success(data.message || "Template deleted.");
      await get().fetchAll();
      return true;
    } catch (e: any) {
      const d = e?.response?.data;
      if (d?.deactivated) {
        toast(d.message, { icon: "⚠️", duration: 7000 });
        await get().fetchAll();
        return false;
      }
      toast.error(err(e, "Couldn't delete the template"));
      return false;
    } finally {
      set({ busy: "" });
    }
  },

  // Fetched as a blob through the authorised endpoint, so the session travels
  // with the request and no storage URL is ever exposed to the browser.
  openFile: async (id, fileName) => {
    set({ busy: "file" });
    try {
      const res = await axiosInstance.get(`/prd-templates/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "prd-template";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(err(e, "Couldn't open the template file"));
    } finally {
      set({ busy: "" });
    }
  },
}));
